import { apiCall } from '../../../services/apiUtil';

const PICKLEBALL_SPORT_TYPE_ID = 2;

// Live scoring actions have no default request timeout upstream in apiCall,
// so on a degraded mobile connection the scorer was waiting on the browser's
// own default fetch timeout (60s+) before seeing "Failed to record" — a long
// stuck loading banner instead of a fast, actionable failure. These are all
// small, fast, interactive calls (record a point, undo, complete a game) —
// there's no legitimate reason for one to take more than a few seconds, so
// fail fast and let the scorer retry immediately instead of staring at a spinner.
const SCORING_TIMEOUT_MS = 8000;
const withTimeout = (options = {}) => ({ ...options, signal: AbortSignal.timeout(SCORING_TIMEOUT_MS) });

export const pickleballGameAPI = {
  create: async (matchData) => {
    const format = matchData.format || 'doubles';
    const payload = {
      sportTypeId: PICKLEBALL_SPORT_TYPE_ID,
      eventId: matchData.eventId,
      format: format,
      gameName: matchData.matchName || null,
      playerA: {
        type: format === 'doubles' ? 'team' : 'user',
        id: 0,
        name: matchData.team1Name,
        players: matchData.team1Players || [],
      },
      playerB: {
        type: format === 'doubles' ? 'team' : 'user',
        id: 0,
        name: matchData.team2Name,
        players: matchData.team2Players || [],
      },
      gameSettings: {
        scoreToWin: matchData.scoreToWin || 11,
        winByTwo: matchData.winByTwo !== false,
        bestOf: matchData.bestOf || 3,
      },
      status: matchData.status || 'setup',
    };

    return apiCall('/games', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getById: async (id) => {
    return apiCall(`/games/${id}`);
  },

  getByEventId: async (eventId) => {
    return apiCall(`/games/event/${eventId}`);
  },

  update: async (matchId, updateData) => {
    return apiCall(`/games/${matchId}`, {
      method: 'PATCH',
      body: JSON.stringify(updateData),
    });
  },

  setInitialServe: async (matchId, serveData) => {
    return apiCall(`/games/${matchId}/pickleball/serve`, withTimeout({
      method: 'PATCH',
      body: JSON.stringify({
        servingTeam: serveData.servingTeam,
        servingPlayerId: serveData.servingPlayerId || null,
      }),
    }));
  },

  // Swaps in a different player for one side of a match mid-game (singles
  // or doubles) — unlike setInitialServe, this corrects server/even-player
  // tracking even after rallies have already been recorded. oldPlayerId
  // identifies which of the side's current players is being replaced;
  // required for doubles, optional for singles (unambiguous).
  swapPlayer: async (matchId, { side, oldPlayerId = null, newPlayer }) => {
    return apiCall(`/games/${matchId}/pickleball/swap-player`, withTimeout({
      method: 'PATCH',
      body: JSON.stringify({ side, oldPlayerId, newPlayer }),
    }));
  },

  recordRally: async (matchId, rallyData) => {
    const payload = {
      winningTeam: rallyData.winningTeam,
      winningShotPlayerId: rallyData.winningShotPlayerId || null,
      shotType: rallyData.shotType || null,
      pointLoserPlayerId: rallyData.pointLoserPlayerId || null,
      isServiceFault: rallyData.isServiceFault || false,
    };

    return apiCall(`/games/${matchId}/pickleball/rallies`, withTimeout({
      method: 'POST',
      body: JSON.stringify(payload),
    }));
  },

  undoLastRally: async (matchId) => {
    return apiCall(`/games/${matchId}/pickleball/rallies/last`, withTimeout({
      method: 'DELETE',
    }));
  },

  completeGame: async (matchId) => {
    return apiCall(`/games/${matchId}/pickleball/game-complete`, withTimeout({
      method: 'POST',
    }));
  },

  // Completes the current game by directly setting its final score —
  // skips rally-by-rally scoring entirely (no rallies get recorded).
  completeGameWithScore: async (matchId, { participantAScore, participantBScore }) => {
    return apiCall(`/games/${matchId}/pickleball/final-score`, {
      method: 'PATCH',
      body: JSON.stringify({ participantAScore, participantBScore }),
    });
  },

  complete: async (matchId, completionData) => {
    return apiCall(`/games/${matchId}/complete`, withTimeout({
      method: 'POST',
      body: JSON.stringify(completionData || {}),
    }));
  },

  deleteGame: async (matchId) => {
    return apiCall(`/games/${matchId}`, {
      method: 'DELETE',
    });
  },

  reset: async (matchId) => {
    return apiCall(`/games/${matchId}/pickleball/reset`, {
      method: 'POST',
    });
  },

  delete: async (matchId) => {
    return apiCall(`/games/${matchId}`, {
      method: 'DELETE',
    });
  },

  validateEventCompletion: async (eventId) => {
    return apiCall(`/games/event/${eventId}/pickleball/validate-completion`);
  },

  pause: async (gameId, { reason, durationMinutes }) => {
    return apiCall(`/games/${gameId}/pause`, withTimeout({
      method: 'POST',
      body: JSON.stringify({ reason, durationMinutes }),
    }));
  },

  resume: async (gameId) => {
    return apiCall(`/games/${gameId}/resume`, withTimeout({
      method: 'POST',
    }));
  },
};

export default pickleballGameAPI;
